import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { useAppDataStore } from '@/stores/app-data'
import type { WorldInterface } from '@/types/worlds'

/**
 * World scope for the `*Finder` element browsers while a character is loaded.
 *
 * The browsers otherwise take their `world.uuid` predicate from the shared
 * `predicateStore.worlds` slot — element scoping that finders and editors write on mount,
 * and that `loadSystemContext` clears on every save. Which world a finder ended up scoped
 * to therefore came down to which components had mounted. This makes it explicit.
 *
 * The rule is ownership-blind, because "which content may this character use" is a property
 * of the character's world, not of who owns it: a GM-assigned character and a character
 * built in an invited world are both scoped to their world.
 *
 * This is NOT the complement of `useCharacterSourceFilter`. Source and world answer different
 * questions and combine freely: source matches where an element was published, world matches
 * which campaign settings it is linked to. `useCharacterSourceFilter` therefore offers the
 * Source picker for every loaded character, world-scoped or not — only the world picker is
 * gated on `!character.world`, because a world-scoped draft already has its world scope from
 * here. (An earlier version of this comment claimed a character "picks a source or inherits
 * its world's scope, never both"; that described a bug where the world rule had been wired to
 * the Source control, fixed in the guided-setup steps and in that composable.)
 *
 * Returns `null` for "no opinion — defer to `predicateStore.worlds`", so the data-manager
 * surfaces that reuse these finders without a character keep their own filtering.
 */
export function useCharacterWorldScope(): { worlds: ComputedRef<WorldInterface[] | null> } {
    const store = useAppDataStore()

    const worlds = computed<WorldInterface[] | null>(() => {
        const character = store.character
        if (!character) return null

        // A worldless character resolves to [] rather than null: it must override a world left
        // in the shared slot by a previously-viewed character, not inherit it.
        const world = character.world ?? null
        return world?.uuid ? [world as WorldInterface] : []
    })

    return { worlds }
}
